import {
  Header,
  ProductHub,
  BuilderFrameIconButton,
  SideBar,
  BuilderFrameButton,
  ProductListButton,
  HeaderLayout,
  BuilderFrameLayout,
  BaseDrawer,
  SideBarCopilotItem,
} from "@zapier/product-nav";
import {
  SUMMARY,
  SideBarSelected,
  isPublicAtom,
  isViewOnlyModeAtom,
  sessionAtom,
  sideBarSelectedAtom,
  isViewerAtom,
  hasZslAtom,
  showZslFeedbackAtom,
  isTemplatePageAtom,
  canvasAtom,
  isViewOnlyDowntimeOverrideAtom,
  hasStreamingProgressStartedAtom,
  isDraftModeIncludingTemplatesAtom,
  isDraftModeExcludingTemplatesAtom,
  closeSidebarSetter,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import { AIStreamCreateModalWrapper } from "@zapier/streaming";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Suspense, useCallback } from "react";
import { Spacer } from "@zapier/design-system";

import { InteractiveName } from "../InteractiveName";
import ShareButton, { LoadingShareButton } from "../ShareButton/ShareButton";
import { ZoomSelect } from "../ZoomSelect";
import { HelpMenu } from "../HelpMenu";
import { SidebarDrawer } from "../SidebarDrawer";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { DiagrammingToolbar } from "../DiagrammingToolbar";
import { AiCopilotBanner } from "../AiCopilotBanner";
import { TemplateFeedback } from "../TemplateFeedback";
import { LoggedOutTemplateBanner } from "./LoggedOutTemplateBanner";
import { TemplatePageBanner } from "../TemplatePageBanner";
import { ProgressBanner } from "../ProgressBanner";
import { useCalculatedDrawerHeight } from "../../hooks/useCalculatedDrawerHeight";

import { ViewOnlyDowntimeBanner } from "./ViewOnlyDowntimeBanner";
import { DraftBanner } from "../DraftBanner";
import styles from "./BuilderNav.module.css";

type Props = {
  children: React.ReactNode;
};

// TODO: Import this from @zapier/product-nav when possible
type HeaderItemProps = {
  component: React.ReactElement | null;
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  showOnTablet?: boolean;
  showOnMobile?: boolean;
};

export function BuilderNav({ children }: Props) {
  const canvas = useAtomValue(canvasAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const sideBarSelected = useAtomValue(sideBarSelectedAtom);
  const session = useAtomValue(sessionAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isPublic = useAtomValue(isPublicAtom);
  const isTemplatePage = useAtomValue(isTemplatePageAtom);
  const [showTemplateFeedback, setShowTemplateFeedback] =
    useAtom(showZslFeedbackAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  const isDraftModeNotTemplates = useAtomValue(
    isDraftModeExcludingTemplatesAtom,
  );
  const toggles = useAtomValue(togglesAtomResettable);

  const { headerRef, drawerRef } = useCalculatedDrawerHeight();

  const handleOnClose = useSetAtom(closeSidebarSetter);

  const handleSideBarItemPress = useAtomCallback(
    useCallback(
      (get, set, selectedItem: SideBarSelected) => {
        const sideBarSelected = get(sideBarSelectedAtom);
        if (sideBarSelected === selectedItem) {
          handleOnClose();
        } else {
          set(sideBarSelectedAtom, selectedItem);
        }
      },
      [handleOnClose],
    ),
  );

  const rightColumnComponents: HeaderItemProps[] = [
    !hasZsl &&
      !isPublic && {
        component: (
          <Suspense fallback={<LoadingShareButton />}>
            <ShareButton />
            <Spacer width={5} />
          </Suspense>
        ),
        hideOnTablet: true,
      },
    session?.is_logged_in &&
      session?.current_account_id === canvas.account_id &&
      isPublic && {
        component: (
          <BuilderFrameButton href={`/app/canvas/${canvas.id}`}>
            Edit
          </BuilderFrameButton>
        ),
      },
    !session?.is_logged_in &&
      isPublic && {
        component: (
          <BuilderFrameButton href={`/app/login?next=/app/canvas/${canvas.id}`}>
            Log in to edit
          </BuilderFrameButton>
        ),
        hideOnTablet: true,
      },
    {
      component: <ZoomSelect />,
      hideOnTablet: true,
    },
    {
      component: <HelpMenu />,
      hideOnTablet: false,
    },
  ].filter(Boolean) as HeaderItemProps[];

  const { SideBarItem } = SideBar;

  // Determine if copilot icon should be shown
  const shouldShowCopilotIcon =
    !isPublic &&
    !isTemplatePage &&
    (toggles.aiCopilotCobuild || isDraftModeNotTemplates);

  return (
    <>
      <BuilderFrameLayout
        header={
          <div ref={headerRef}>
            <Header isResponsive={true}>
              <HeaderLayout
                leftColumnComponents={[
                  {
                    component: (
                      <BuilderFrameIconButton
                        icon="navHome"
                        label="Home"
                        href="/"
                        aria-label="Home"
                      />
                    ),
                  },
                  { component: <Spacer width={15} />, hideOnMobile: true },
                  {
                    component: (
                      <ProductHub
                        productName="Canvas"
                        // TODO: Add this footer in later when we get copy requirements
                        // footer={
                        //   !account?.plan_info.allow_companies ? (
                        //     <>
                        //       You are on the{" "}
                        //       <strong>{account?.plan_info.name}</strong> plan.
                        //       Automate at scale with our <a href="/">Company</a>{" "}
                        //       plan
                        //     </>
                        //   ) : null
                        // }
                      />
                    ),
                    hideOnMobile: true,
                  },
                  { component: <Spacer width={4} />, hideOnTablet: true },
                  {
                    component: <ProductListButton productName="Canvas" />,
                    hideOnTablet: true,
                  },
                  { component: <Spacer width={10} />, hideOnTablet: true },
                  // TODO: Add this in later
                  // {
                  //   component: (
                  //     <UpgradeCTA
                  //       planName={account?.plan_info.name || "Free"}
                  //       productName="Canvas"
                  //       upgradeHref="https://zapier.com/free-trial/"
                  //     />
                  //   ),
                  //   hideOnTablet: true,
                  // },
                ]}
                centerColumnComponents={[
                  {
                    component: (
                      <InteractiveName
                        canvasName={canvas.name}
                        ownerId={canvas.customuser_id!}
                        permissions={isViewOnlyMode ? "view" : "edit"}
                      />
                    ),
                  },
                ]}
                rightColumnComponents={rightColumnComponents}
              />
            </Header>
            <Banner />
          </div>
        }
        sidebar={
          <div style={{ display: "flex" }}>
            <SideBar
              isResponsive={true}
              bottomElement={
                shouldShowCopilotIcon ? (
                  <SideBarCopilotItem
                    onPress={() => handleSideBarItemPress("copilot")}
                    tooltipTitle="Copilot"
                    selected={sideBarSelected === "copilot"}
                  />
                ) : null
              }
            >
              <SideBarItem
                name={SUMMARY}
                icon="fileGuide"
                tooltipTitle={capitalizeFirstLetter(SUMMARY)}
                selected={sideBarSelected === SUMMARY}
                onPress={() => handleSideBarItemPress(SUMMARY)}
                isResponsive={true}
              />
              {(session?.is_staff || session?.is_masquerade) && (
                <SideBarItem
                  name="devtools"
                  icon="actionBug"
                  tooltipTitle="DevTools"
                  selected={sideBarSelected === "devTools"}
                  onPress={() => handleSideBarItemPress("devTools")}
                  isResponsive={true}
                />
              )}
            </SideBar>
          </div>
        }
        mainContent={
          <div style={{ display: "flex", height: "100%", width: "100%" }}>
            <div ref={drawerRef}>
              <BaseDrawer
                onClose={handleOnClose}
                isOpen={!!sideBarSelected}
                className={styles.drawerOverrides}
                isFixed={true}
                isResponsive={true}
                closeOnOutsideClick={false}
              >
                <SidebarDrawer onCloseClick={handleOnClose} />
              </BaseDrawer>
            </div>
            {children}
            {isDraftModeNotTemplates && <DraftBanner />}
            {!isViewOnlyMode && !isDraftModeIncludingTemplates && (
              <Suspense fallback={null}>
                <DiagrammingToolbar />
              </Suspense>
            )}
          </div>
        }
      />
      <AIStreamCreateModalWrapper />
      {showTemplateFeedback && (
        <TemplateFeedback onClose={() => setShowTemplateFeedback(false)} />
      )}
    </>
  );
}

function Banner() {
  const isViewer = useAtomValue(isViewerAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  const session = useAtomValue(sessionAtom);
  const isTemplatePage = useAtomValue(isTemplatePageAtom);
  const viewOnlyDowntimeOverride = useAtomValue(isViewOnlyDowntimeOverrideAtom);
  const hasStreamingProgressStarted = useAtomValue(
    hasStreamingProgressStartedAtom,
  );
  if (viewOnlyDowntimeOverride) {
    return <ViewOnlyDowntimeBanner />;
  }
  if (hasZsl && isDraftModeIncludingTemplates) {
    if (!session?.is_logged_in) {
      return <LoggedOutTemplateBanner />;
    }
    if (!isViewer) {
      if (isTemplatePage) {
        return <TemplatePageBanner />;
      }

      return <AiCopilotBanner />;
    }
  }

  if (hasStreamingProgressStarted) {
    return <ProgressBanner />;
  }

  return null;
}
